import { AddSelectProperties } from '@crossbeam/types';

import { capitalize } from 'lodash';

import { isOnboardingDemoSource } from '@/components/onboarding/onboardingDemoReport';

import {
  SOURCE_STATUS_MAP,
  getSourceTableLabel,
} from '@/constants/data_sources';
import type { Feed } from '@/types/feeds';
import type { Source } from '@/types/sources';
import { sortByKey } from '@/utils';

export interface DataSourceOption {
  label: string;
  value: number | 'add-new-source';
  svg?: string;
  icon?: string[];
  iconColor?: string;
}

export type SourceFieldOption = AddSelectProperties<
  { name: string; disabled?: boolean; loading?: boolean },
  number | undefined
>;

export function buildDataSourceOptions(
  feeds: Feed[],
  includeAddNew = true,
): DataSourceOption[] {
  const options: DataSourceOption[] = feeds.map((feed) => ({
    label: feed.integration.friendly_name,
    value: feed.id,
    svg: getDataSourceSvg(feed),
  }));

  if (includeAddNew) {
    options.push({
      label: 'Add New Data Source',
      value: 'add-new-source' as const,
      icon: ['fak', 'add'],
      iconColor: 'text-neutral-text-weak',
    });
  }

  return options;
}

export function getDataSourceSvg(feed: Feed): string {
  const name = feed.integration.type;
  return `${name}Icon`;
}

export function buildSourceFieldOptions(
  sources: Source[],
  selectedFeedId: number | null,
  selectedFeed: Feed | null,
  isCSVSelected: boolean,
  currentUploadNames: string[],
): SourceFieldOption[] {
  if (!selectedFeedId || !selectedFeed) return [];

  let matchingSources = sources.filter(
    (source) =>
      (source.feed_id === selectedFeedId ||
        source.schema === selectedFeed.schema_name) &&
      source.is_base_table &&
      source.sync_enabled === true &&
      source.id !== undefined,
  );

  if (isCSVSelected) {
    matchingSources = matchingSources.filter((source) =>
      currentUploadNames.includes(source.table),
    );

    // Once the user has uploaded a real CSV, drop the onboarding demo file.
    if (matchingSources.some((source) => !isOnboardingDemoSource(source))) {
      matchingSources = matchingSources.filter(
        (source) => !isOnboardingDemoSource(source),
      );
    }
  }

  return matchingSources
    .map((source) => ({
      name: source.table,
      label: getSourceTableLabel(source.table, capitalize(source.table)),
      value: source.id,
      ...(isCSVSelected && {
        disabled: isSourceDisabledAndLoading(source),
        loading: isSourceDisabledAndLoading(source),
      }),
    }))
    .sort(sortByKey('name'));
}

function isSourceDisabledAndLoading(source: Source): boolean {
  if (!source.status) return false;

  const processStatuses = SOURCE_STATUS_MAP.process as readonly string[];
  const deletingStatuses = SOURCE_STATUS_MAP.deleting as readonly string[];

  return (
    processStatuses.includes(source.status) ||
    deletingStatuses.includes(source.status)
  );
}
